/**
 * Actions, for one person (WALLIYO-0131).
 *
 * The console tab of Feature Enablements, read over actions. Search somebody
 * by the number on their badge, see every action, and the Source column says
 * where each answer came from — a role, the floor under all staff, or an
 * override set on them, which is the only one this screen can take away.
 *
 * An action is not a third kind of switch. What moves is the feature that
 * unlocks it and, where the action needs one, the permission that gates it —
 * so a row says which of the two decided the answer rather than pretending
 * there is one dial.
 */

import type { ActionSubject, StaffActionItem } from "@wallyo/contracts/console/hr";
import { Fragment } from "react";

import { actionName, categoryName, t, type MessageId } from "../../i18n";
import { formatDateTime } from "../../i18n/format";
import { ErrorNotice } from "../../shell/ErrorNotice";
import { formatLimits } from "../team/team-format";

export type ActionsState =
  | { status: "idle" }
  | { status: "loading" }
  | { status: "ready"; data: ActionsData }
  | { status: "failed"; error: unknown };

export type ActionsData = {
  actions: readonly StaffActionItem[];
  subject: ActionSubject;
};

const SOURCE_LABEL: Record<StaffActionItem["source"], MessageId> = {
  staffMember: "features.staffSource.override",
  staffRole: "features.staffSource.byRole",
  allStaff: "features.staffSource.allStaff",
  default: "features.source.default",
};

const NOT_EDITABLE: Record<NonNullable<StaffActionItem["notEditable"]>, MessageId> = {
  own_account: "features.locked.ownAccount",
  critical_requires_owner: "features.locked.grant",
  no_feature_to_switch: "actions.locked.noFeature",
};

function initialsOf(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? "")
    .join("");
}

/** Who the table is about, always: an employee, or a role. */
function SubjectPill({ subject }: { subject: ActionSubject }) {
  return (
    <span className="subject-pill">
      <span className="subject-initials" aria-hidden="true">
        {initialsOf(subject.name)}
      </span>
      <span className="subject-name">{subject.name}</span>
      <span className="subject-sep" aria-hidden="true" />
      {subject.kind === "employee" ? (
        <>
          <span className="mono">{subject.uuid}</span>
          <span className="subject-sep" aria-hidden="true" />
          <span className="subject-kind-tag">{subject.roles.join(", ") || t("actions.noRoles")}</span>
        </>
      ) : (
        <span className="subject-kind-tag">{t("roles.rail.people", { count: String(subject.memberCount) })}</span>
      )}
    </span>
  );
}

/**
 * A switch that is drawn even when it cannot move, with the reason beside it.
 * A control that vanishes teaches nobody why.
 */
function Switch({ action, value, onToggle }: { action: StaffActionItem; value: boolean; onToggle: () => void }) {
  const locked = !action.editable;
  return (
    <button
      type="button"
      className={`action-switch${value ? " on" : ""}${locked ? " locked" : ""}`}
      role="switch"
      aria-checked={value}
      aria-label={actionName(action.actionId)}
      disabled={locked}
      onClick={onToggle}
    >
      <span className="action-switch-knob" aria-hidden="true" />
    </button>
  );
}

/** Where the answer came from, and the roles when a role is the answer. */
function Source({ action }: { action: StaffActionItem }) {
  const label =
    action.source === "staffRole" && action.grantedBy.length > 0
      ? t("features.staffSource.byRoleNamed", { roles: action.grantedBy.join(", ") })
      : t(SOURCE_LABEL[action.source]);
  return (
    <>
      <span>{label}</span>
      {action.source === "staffMember" ? (
        <>
          {" "}
          <span className="pill info">{t("features.staffSource.override")}</span>
        </>
      ) : null}
      {/* Which of the two said no. Without this a row reads "off, by default"
          when the truth is they have the capability and not the permission. */}
      {action.blockedBy === "permission_not_held" ? (
        <div className="foot">{t("actions.blocked.permission")}</div>
      ) : null}
    </>
  );
}

/** Why somebody set it, or why it cannot be moved. */
function Reason({ action }: { action: StaffActionItem }) {
  const locked = action.notEditable ? t(NOT_EDITABLE[action.notEditable]) : null;
  return (
    <>
      {action.reason ? <bdi>{action.reason}</bdi> : null}
      {action.expiresAt ? (
        <div className="foot">{t("actions.until", { when: formatDateTime(action.expiresAt) })}</div>
      ) : null}
      {locked ? <div className="foot">{locked}</div> : null}
      {!action.reason && !action.expiresAt && !locked ? <span className="muted-cell">—</span> : null}
    </>
  );
}

export function ActionsByPerson(props: {
  state: ActionsState;
  /** The search box, for an employee. Absent when a role is being picked. */
  query?: string;
  onQuery?: (query: string) => void;
  onSearch?: () => void;
  /** The picker, for a role. */
  picker?: React.ReactNode;
  /** People a name matched, when more than one did. */
  matches?: readonly { uuid: number; name: string; roles: readonly string[] }[];
  onPick?: (uuid: number) => void;
  /** Switches moved but not yet saved: the row shows the draft's answer. */
  draft: Readonly<Record<string, boolean | null>>;
  onToggle: (action: StaffActionItem) => void;
  onClear: (action: StaffActionItem) => void;
}) {
  const { state } = props;
  const data = state.status === "ready" ? state.data : null;

  // Grouped by area, the way the catalogue groups them, so a long list reads
  // as a handful of short ones.
  const groups = new Map<string, StaffActionItem[]>();
  for (const action of data?.actions ?? []) {
    const list = groups.get(action.area);
    if (list) list.push(action);
    else groups.set(action.area, [action]);
  }

  return (
    <>
      <form
        className="hr-search"
        onSubmit={(event) => {
          event.preventDefault();
          props.onSearch?.();
        }}
      >
        {props.picker ?? (
          <>
            <label className="field">
              <span className="label">{t("actions.find")}</span>
              <input
                type="search"
                value={props.query ?? ""}
                placeholder={t("actions.findPlaceholder")}
                onChange={(event) => props.onQuery?.(event.currentTarget.value)}
              />
            </label>
            <button className="btn" type="submit">
              {t("directory.go")}
            </button>
          </>
        )}
        <div className="grow" />
        {data ? <SubjectPill subject={data.subject} /> : null}
      </form>

      {/* More than one person matched: they pick, rather than the screen
          guessing which one was meant. */}
      {(props.matches?.length ?? 0) > 0 ? (
        <section className="card">
          <h2>{t("actions.matches")}</h2>
          <ul className="hr-plain">
            {(props.matches ?? []).map((person) => (
              <li key={person.uuid} className="role-holder">
                <strong>{person.name}</strong>
                <span className="mono foot">{person.uuid}</span>
                <span className="foot">{person.roles.join(", ")}</span>
                <div className="grow" />
                <button className="btn quiet small" type="button" onClick={() => props.onPick?.(person.uuid)}>
                  {t("directory.open")}
                </button>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {state.status === "idle" && (props.matches?.length ?? 0) === 0 ? (
        <div className="empty">{t("actions.nobody")}</div>
      ) : null}

      {state.status === "loading" ? (
        <div className="empty" role="status">
          <span className="spinner" aria-hidden="true" />
          <span>{t("hr.loading")}</span>
        </div>
      ) : null}

      {state.status === "failed" ? (
        <div className="audit-error">
          <p className="foot">{t("hr.failed")}</p>
          <ErrorNotice error={state.error} />
          <div className="btns">
            <button className="btn" type="button" onClick={() => props.onSearch?.()}>
              {t("hr.retry")}
            </button>
          </div>
        </div>
      ) : null}

      {data ? (
        <section className="card">
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>{t("actions.col.action")}</th>
                  <th>{t("actions.col.on")}</th>
                  <th>{t("actions.col.source")}</th>
                  <th>{t("actions.col.reason")}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {[...groups.entries()].map(([area, actions]) => (
                  <Fragment key={area}>
                    <tr className="cat">
                      <td colSpan={5}>{categoryName(area as never)}</td>
                    </tr>
                    {actions.map((action) => (
                      <tr
                        key={action.actionId}
                        className={props.draft[action.actionId] !== undefined ? "dirty" : undefined}
                      >
                        <td>
                          <strong>{actionName(action.actionId)}</strong>
                          {action.mustAsk ? (
                            <>
                              {" "}
                              <span className="pill warn">
                                {t("actions.mustAsk", { limit: formatLimits(action.limits) })}
                              </span>
                            </>
                          ) : null}
                        </td>
                        <td>
                          <Switch
                            action={action}
                            value={props.draft[action.actionId] ?? action.value}
                            onToggle={() => props.onToggle(action)}
                          />
                          {props.draft[action.actionId] !== undefined ? (
                            <div className="foot">{t("actions.pending")}</div>
                          ) : null}
                        </td>
                        <td>
                          <Source action={action} />
                        </td>
                        <td>
                          <Reason action={action} />
                        </td>
                        <td className="num">
                          {action.source === "staffMember" && action.editable ? (
                            <button className="btn quiet small" type="button" onClick={() => props.onClear(action)}>
                              {t("actions.clear")}
                            </button>
                          ) : null}
                        </td>
                      </tr>
                    ))}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
          <p className="foot">{t("actions.onlyOverrides")}</p>
        </section>
      ) : null}
    </>
  );
}
